import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/cn'
import { queryKeys } from '@/lib/query-client'
import { fetchDashboardStats } from '@/features/dashboard/api'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import styles from './AppShell.module.css'

const COLLAPSE_KEY = 'dfm.sidebar.collapsed'

export function AppShell() {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true'
    } catch {
      return false
    }
  })
  const [mobileOpen, setMobileOpen] = useState(false)

  // The approval queue drives the sidebar badge. It is already fetched for the
  // overview, so this shares that cache rather than adding a second request.
  const { data: stats } = useQuery({
    queryKey: queryKeys.dashboard.stats(14),
    queryFn: () => fetchDashboardStats(14),
    staleTime: 60_000,
  })

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed))
    } catch {
      // A browser that refuses storage just forgets the preference.
    }
  }, [collapsed])

  // Navigating on a phone should close the panel it was navigated from.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <div className={cn(styles.shell, collapsed && styles.collapsed)}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((current) => !current)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        pendingRequests={stats?.pendingTripRequests ?? 0}
      />

      <div className={styles.main}>
        <Topbar onOpenMobileNav={() => setMobileOpen(true)} />
        <main className={styles.content} id="main-content" tabIndex={-1}>
          <div className={styles.container}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
